import { PATIENT_CHECKED_IN_URL } from 'encounter/constants';
import { useMemo } from 'react';
import { useNavigate as useReactRouterNavigate } from 'react-router-dom';

type Collection = {
  [id: string]: any;
};

export const isCollectionEmpty = (data: Collection) => {
  return Object.keys(data).filter((key) => !!data[key]).length < 1;
};

export const areCollectionsEqual = (
  primary: Collection,
  secondary: Collection
) => {
  return Object.keys(primary).every((key) => {
    return primary[key] === secondary[key];
  });
};

export function upperCase(str: string) {
  if (!str) return str;

  return str.toUpperCase();
}

export function isArrayEmpty(data) {
  if (typeof data !== 'undefined') {
    return data.every((item) => item === '');
  }
  return true;
}

export function getProblemStatus(data) {
  if (typeof data !== 'undefined') {
    return data?.clinicalStatus?.coding?.[0]?.display;
  }
  return true;
}

export function getAllergyExtension(data) {
  if (data?.extension) {
    return data?.extension?.find((item) => item?.url === 'TYPE')?.valueString;
  } else return '';
}

export function getEncounterExtensionValue(
  extensions: fhir.Extension[],
  url: fhir.uri = PATIENT_CHECKED_IN_URL,
  valueType: string = 'valueDateTime'
) {
  if (Array.isArray(extensions) && extensions.length > 0) {
    return extensions.find((item) => item.url === url)?.[valueType];
  }
  return null;
}

export const deepCopyObject = (srcObject) =>
  JSON.parse(JSON.stringify(srcObject));

export const debounce = (callback, delay) => {
  let timer;
  return {
    function: function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), delay);
    },
    cancel: () => {
      clearTimeout(timer);
    },
  };
};

export const detectDevice = () => {
  let deviceOS = 'unknownos';
  if (navigator?.appVersion.indexOf('Win') !== -1) deviceOS = 'pc';
  if (navigator?.appVersion.indexOf('Mac') !== -1) deviceOS = 'tablet';
  if (navigator?.appVersion.indexOf('Android') !== -1) deviceOS = 'tablet';
  if (deviceOS === 'tablet' && navigator?.maxTouchPoints < 2) deviceOS = 'pc';
  return deviceOS;
};

export function removeReferenceResource(value) {
  return JSON.parse(JSON.stringify(value), (key, value) => {
    if (key === 'resource') {
      return undefined;
    }

    return value;
  });
}

export const findIndex = (array, condition) => {
  let refillIndex = -1;
  for (let index = 0; index < array.length; index++) {
    if (condition(array[index])) {
      refillIndex = index;
      break;
    }
  }
  return refillIndex;
};
export const flattenedCategoryCodeableConept = (items, system) => {
  const modifiedObject = items?.CodeSystem?.concept?.map((category) => {
    return {
      code: category?.code,
      display: category?.display,
      concept: category?.concept?.map((item) => {
        return {
          coding: [
            {
              code: item.code,
              display: item.display,
              system: system,
            },
          ],
          text: '',
        };
      }),
    };
  });
  return modifiedObject?.flat();
};

export function getCodeableConceptCode(
  concept?: fhir.CodeableConcept
): string | undefined {
  return concept?.coding?.[0]?.code;
}

export function getCodeableConceptDisplay(
  concept?: fhir.CodeableConcept
): string | undefined {
  return concept?.coding?.[0]?.display;
}

export function sortCodeableConceptDisplay(
  conceptArr: fhir.CodeableConcept[],
  sortAscending: boolean
): fhir.CodeableConcept[] {
  return conceptArr?.sort((option1, option2) => {
    const option1Display = getCodeableConceptDisplay(option1) || '';
    const option2Display = getCodeableConceptDisplay(option2) || '';
    const isGreater = option1Display > option2Display ? 1 : -1;
    return sortAscending ? isGreater : isGreater * -1;
  });
}

export function getCodeableConceptSystem(
  concept?: fhir.CodeableConcept
): string | undefined {
  return concept?.coding?.[0]?.system;
}

export function getTimezoneNameByState(state: string) {
  return (
    {
      AZ: 'America/Phoenix',
      CA: 'America/Los_Angeles',
      FL: 'America/New_York',
      NJ: 'America/New_York',
      NV: 'America/Los_Angeles',
      OH: 'America/New_York',
      PR: 'America/Puerto_Rico',
      TX: 'America/Chicago',
      UT: 'America/Denver',
      HI: 'Pacific/Honolulu',
    }[state] ?? 'America/New_York' // fallback to 'America/New_York'
  );
}

export const downloadFileFromUrl = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const objectUrl = globalThis.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();

    setTimeout(() => {
      globalThis.URL.revokeObjectURL(objectUrl);
    }, 100);
  } catch (error) {}
};

export const createEnhancedNavigate = (originalNavigate: any) => {
  return (to: any, options?: any) => {
    if (typeof to === 'number' && to < 0) {
      setTimeout(() => {
        try {
          const currentPath = window.location.pathname;
          let targetPath;

          if (currentPath.endsWith('/edit')) {
            targetPath = currentPath.replace('/edit', '/info');
          } else {
            const pathSegments = currentPath.split('/').filter(Boolean);
            const stepsBack = Math.abs(to);
            const targetSegments = pathSegments.slice(0, -stepsBack);
            targetPath = '/' + targetSegments.join('/');
          }
          originalNavigate(targetPath, { replace: false });
        } catch (error) {
          try {
            if (Math.abs(to) === 1) {
              window.history.back();
            } else {
              window.history.go(to);
            }
          } catch (historyError) {
            setTimeout(() => {
              originalNavigate(to, options);
            }, 50);
          }
        }
      }, 0);
    } else {
      setTimeout(() => {
        try {
          originalNavigate(to, options);
        } catch (error) {
          originalNavigate(to);
        }
      }, 0);
    }
  };
};

export const useNavigate = () => {
  const originalNavigate = useReactRouterNavigate();
  return useMemo(() => {
    return createEnhancedNavigate(originalNavigate);
  }, [originalNavigate]);
};

export const shouldReloadWoundAnalyzerPage = (
  signedURL?: string,
  currentPathname: string = window.location.pathname
) => {
  if (!signedURL) return false;
  const currentPath = currentPathname.replace(/^\//, '').replace(/\/$/, '');
  const targetPath = signedURL.replace(/^\//, '').replace(/\/$/, '');

  // Match same wound assessment even when one URL includes admissions/... and the other does not.
  // patients/:patientId/(admissions/:admissionId/)?encounter/:encounterId/wounds/:woundId/assessment/...
  const woundAssessmentKey = (path: string) => {
    const match =
      /^patients\/([^/]+)\/(?:admissions\/[^/]+\/)?encounter\/([^/]+)\/wounds\/([^/]+)\/assessment(?:\/|$)/.exec(
        path
      );
    return match ? `${match[1]}/${match[2]}/${match[3]}` : null;
  };

  const currentKey = woundAssessmentKey(currentPath);
  const targetKey = woundAssessmentKey(targetPath);
  return Boolean(currentKey && targetKey && currentKey === targetKey);
};

/** Soft-refresh the assessment right panel (Form listens and refetches). */
export const WOUND_ANALYZER_REFRESH_EVENT = 'wound-analyzer:refresh';

export const refreshWoundAnalyzerPanel = () => {
  window.dispatchEvent(new CustomEvent(WOUND_ANALYZER_REFRESH_EVENT));
};

export const navigateToWoundAnalyzerResult = (signedURL?: string) => {
  const path = signedURL?.replace(/^\//, '');
  if (!path) return;

  if (shouldReloadWoundAnalyzerPage(path)) {
    refreshWoundAnalyzerPanel();
  } else {
    window.location.assign(`/${path}`);
  }
};